<template>
  <section
    id="products"
    class="py-16 sm:py-20 bg-annapolis-charcoal"
    aria-labelledby="products-heading"
  >
    <div class="container mx-auto px-4 sm:px-6">
      <!-- Section Header -->
      <div class="text-center mb-12 sm:mb-16">
        <h2
          id="products-heading"
          class="text-fluid-3xl sm:text-fluid-4xl md:text-fluid-5xl font-bold text-white mb-3 sm:mb-4"
        >
          Our Products
        </h2>
        <p class="text-fluid-base sm:text-fluid-lg text-gray-400 max-w-2xl mx-auto">
          Security and infrastructure tools for modern software teams
        </p>
      </div>

      <!-- Products Grid -->
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-7xl mx-auto"
        role="list"
        aria-label="List of products"
      >
        <!-- SteamSafe Card -->
        <div
          class="bg-annapolis-navy rounded-xl p-8 border border-annapolis-teal/20 hover:border-annapolis-teal/50 transition-all duration-300 shadow-xl flex flex-col"
          role="listitem"
          aria-labelledby="steamsafe-heading"
        >
          <div class="flex-1">
            <div class="flex items-center mb-4 sm:mb-6">
            <div class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-annapolis-teal-dark to-annapolis-teal rounded-lg flex items-center justify-center mr-3 sm:mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-6 sm:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3
              id="steamsafe-heading"
              class="text-2xl sm:text-3xl font-bold text-white"
            >
              SteamSafe
            </h3>
          </div>

          <p class="text-gray-300 mb-4 sm:mb-6 text-base sm:text-lg leading-relaxed">
            Protect your game keys. Identify leaks. Maintain accountability.
          </p>

          <div class="mb-4 sm:mb-6">
            <h4 class="text-annapolis-teal font-semibold mb-2 sm:mb-3 text-sm sm:text-base">Key Features:</h4>
            <ul class="space-y-1 sm:space-y-2 text-gray-300 text-sm sm:text-base">
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Military-grade HMAC-SHA256 key hashing</span>
              </li>
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Track recipients, dates, and distribution purposes</span>
              </li>
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Leak detection across gray market sites</span>
              </li>
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Cross-platform: Windows, macOS, Linux</span>
              </li>
            </ul>
          </div>

          <div class="mb-4 sm:mb-6">
            <div class="flex items-baseline gap-2">
              <span class="text-2xl sm:text-3xl font-bold text-annapolis-teal">$29.99</span>
              <span class="text-gray-400 text-sm sm:text-base">one-time purchase</span>
            </div>
            </div>
          </div>

          <a
            href="https://annapolislabs.gumroad.com/l/SteamSafe"
            target="_blank"
            rel="noopener noreferrer"
            class="block w-full text-center px-4 sm:px-6 py-3 bg-annapolis-teal hover:bg-annapolis-teal/90 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-annapolis-teal focus:ring-offset-2 focus:ring-offset-annapolis-navy touch-target"
            aria-label="Buy SteamSafe on Gumroad for $29.99"
          >
            Buy on Gumroad
          </a>
        </div>

        <!-- Licensing Server Card -->
        <div
          class="bg-annapolis-navy rounded-xl p-8 border border-annapolis-teal/20 hover:border-annapolis-teal/50 transition-all duration-300 shadow-xl flex flex-col"
          role="listitem"
          aria-labelledby="licensing-heading"
        >
          <div class="flex-1">
            <div class="flex items-center mb-4 sm:mb-6">
            <div class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-annapolis-teal-dark to-annapolis-teal rounded-lg flex items-center justify-center mr-3 sm:mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-6 sm:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3
              id="licensing-heading"
              class="text-2xl sm:text-3xl font-bold text-white"
            >
              Licensing Server
            </h3>
          </div>

          <p class="text-gray-300 mb-4 sm:mb-6 text-base sm:text-lg leading-relaxed">
            Licensing-as-a-Service for software vendors. Focus on building great software, we'll handle the licensing.
          </p>

          <div class="mb-4 sm:mb-6">
            <h4 class="text-annapolis-teal font-semibold mb-2 sm:mb-3 text-sm sm:text-base">What We Offer:</h4>
            <ul class="space-y-1 sm:space-y-2 text-gray-300 text-sm sm:text-base">
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Simple API integration</span>
              </li>
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>License generation and validation</span>
              </li>
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Automated distribution</span>
              </li>
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Flexible revenue models</span>
              </li>
            </ul>
          </div>
          </div>

          <div class="mb-4 sm:mb-6 p-3 sm:p-4 bg-annapolis-teal/10 border border-annapolis-teal/30 rounded-lg">
            <p class="text-annapolis-teal font-semibold text-sm sm:text-base">Early Access Available</p>
            </div>

          <a
            href="#contact"
            class="block w-full text-center px-4 sm:px-6 py-3 bg-transparent border-2 border-annapolis-teal text-annapolis-teal hover:bg-annapolis-teal hover:text-white font-semibold rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-annapolis-teal focus:ring-offset-2 focus:ring-offset-annapolis-navy touch-target"
            aria-label="Request early access to Licensing Server"
          >
            Request Early Access
          </a>
        </div>

        <!-- Lineage Card -->
        <div
          class="bg-annapolis-navy rounded-xl p-8 border border-annapolis-teal/20 hover:border-annapolis-teal/50 transition-all duration-300 shadow-xl flex flex-col"
          role="listitem"
          aria-labelledby="lineage-heading"
        >
          <div class="flex-1">
            <div class="flex items-center mb-4 sm:mb-6">
            <div class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-annapolis-teal-dark to-annapolis-teal rounded-lg flex items-center justify-center mr-3 sm:mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-6 sm:w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
              </svg>
            </div>
            <h3
              id="lineage-heading"
              class="text-2xl sm:text-3xl font-bold text-white"
            >
              Lineage
            </h3>
          </div>

          <p class="text-gray-300 mb-4 sm:mb-6 text-base sm:text-lg leading-relaxed">
            Modern requirements management system. Track, organize, and manage project requirements with hierarchical structure and bi-directional linking.
          </p>

          <div class="mb-4 sm:mb-6">
            <h4 class="text-annapolis-teal font-semibold mb-2 sm:mb-3 text-sm sm:text-base">Key Features:</h4>
            <ul class="space-y-1 sm:space-y-2 text-gray-300 text-sm sm:text-base">
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Hierarchical organization with parent-child relationships</span>
              </li>
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Bi-directional linking and version history</span>
              </li>
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Role-based access control and multi-project support</span>
              </li>
              <li class="flex items-start">
                <span class="text-annapolis-teal mr-2" aria-hidden="true">✓</span>
                <span>Markdown support with export to CSV, JSON, and Markdown</span>
              </li>
            </ul>
          </div>
          </div>

          <div class="mb-4 sm:mb-6 p-3 sm:p-4 bg-annapolis-teal/10 border border-annapolis-teal/30 rounded-lg">
            <p class="text-annapolis-teal font-semibold text-sm sm:text-base">Open Source</p>
            </div>

          <a
            href="https://github.com/AnnapolisLabs/Lineage"
            target="_blank"
            rel="noopener noreferrer"
            class="block w-full text-center px-4 sm:px-6 py-3 bg-transparent border-2 border-annapolis-teal text-annapolis-teal hover:bg-annapolis-teal hover:text-white font-semibold rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-annapolis-teal focus:ring-offset-2 focus:ring-offset-annapolis-navy touch-target"
            aria-label="View Lineage project on GitHub"
          >
            View on GitHub
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/**
 * ProductsSection component displays the products grid with SteamSafe, Licensing Server, and Lineage
 * @component
 * @example
 * <ProductsSection />
 */
defineOptions({
  name: 'ProductsSection'
})
</script>
